'use client';

import React, {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  startTransition,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { TextInput } from '@/components/ui/text-input';
import { useTranslations } from 'next-intl';

export type MinMaxValueType = {
  min?: number;
  max?: number;
};

type Props = {
  value: MinMaxValueType;
  onChange: (val: MinMaxValueType) => void;
};

const INVALID_NUMBER_KEYS = ['-', '+', 'e', 'E'];

const sanitizeNumberInput = (rawValue: string): string =>
  rawValue
    .replace(',', '.')
    .replace(/[^\d.]/g, '')
    .replace(/(\..*)\./g, '$1');

const parseFloatValue = (rawValue: string): number | undefined => {
  if (rawValue === '' || rawValue === '.') {
    return undefined;
  }

  return Number(rawValue);
};

const toDraft = (value?: number) => (value === undefined ? '' : String(value));

export const FiltersMinMax: React.FC<Props> = ({ value, onChange }) => {
  const t = useTranslations('filters');
  const [minDraft, setMinDraft] = useState(() => toDraft(value.min));
  const [maxDraft, setMaxDraft] = useState(() => toDraft(value.max));

  useEffect(() => {
    setMinDraft((draft) => (parseFloatValue(draft) === value.min ? draft : toDraft(value.min)));
  }, [value.min]);

  useEffect(() => {
    setMaxDraft((draft) => (parseFloatValue(draft) === value.max ? draft : toDraft(value.max)));
  }, [value.max]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (INVALID_NUMBER_KEYS.includes(e.key)) {
      e.preventDefault();
    }
  }, []);

  const onPaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = e.clipboardData.getData('text').replace(',', '.');

    if (!/^\d*\.?\d*$/.test(pastedValue)) {
      e.preventDefault();
    }
  }, []);

  const onChangeMin = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const draft = sanitizeNumberInput(e.target.value);
      setMinDraft(draft);
      startTransition(() => {
        onChange({ min: parseFloatValue(draft), max: value.max });
      });
    },
    [value.max, onChange],
  );

  const onChangeMax = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const draft = sanitizeNumberInput(e.target.value);
      setMaxDraft(draft);
      startTransition(() => {
        onChange({ min: value.min, max: parseFloatValue(draft) });
      });
    },
    [value.min, onChange],
  );

  return (
    <div className='w-full flex gap-[8px] '>
      <div className='w-1/2'>
        <TextInput
          label={t('priceFrom')}
          type='text'
          inputMode='decimal'
          pattern='[0-9]*[.,]?[0-9]*'
          value={minDraft}
          onChange={onChangeMin}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
      <div className='w-1/2'>
        <TextInput
          label={t('priceTo')}
          type='text'
          inputMode='decimal'
          pattern='[0-9]*[.,]?[0-9]*'
          value={maxDraft}
          onChange={onChangeMax}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
    </div>
  );
};
